import { apiClient } from "../../lib/http/apiClient";
import type { AuthUser, OnboardingStatus } from "../auth/types";
export interface ProfileUpdatePayload {
  name?: string;
  bio?: string;
  profile_image?: string;
  phone?: string;
  whatsapp_number?: string;
  country?: string;
  country_code?: string;
  city?: string;
  state?: string;
  address?: string;
  pincode?: string;
  timezone?: string;
  language?: string;
  website?: string;
  social_links?: Record<string, string>;
  agency_name?: string;
  seller_types?: string[];
  business_registration_id?: string;
  tax_id?: string;
  gst_number?: string;
  pan_tan?: string;
  company_email?: string;
  travel_style?: string[];
  personality?: string[];
  onboarding_steps?: Array<{
    key: string;
    completed: boolean;
    skipped: boolean;
    data?: Record<string, unknown>;
  }>;
  current_onboarding_step?: number;
  seller_preferences?: {
    countries?: string[];
    cities?: string[];
    activities?: string[];
    segments?: string[];
  } | null;
  seller_profile?: {
    seller_subtypes?: string[] | null;
    countries?: string[] | null;
    cities?: string[] | null;
    segments?: string[] | null;
    min_group_pax?: number | null;
    max_group_pax?: number | null;
  } | null;
}

export interface PresignedUploadResponse {
  upload_url: string;
  key: string;
  expires_in: number;
  content_type: string;
}

export const VALID_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
export const VALID_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

export const usersService = {
  async getCurrentProfile(): Promise<AuthUser> {
    const { data } = await apiClient.get<AuthUser>("/api/v1/profile");
    return data;
  },

  async updateProfile(payload: ProfileUpdatePayload): Promise<AuthUser> {
    const { data } = await apiClient.put<AuthUser>("/api/v1/profile", payload);
    return data;
  },

  async getOnboardingStatus(): Promise<OnboardingStatus> {
    const { data } = await apiClient.get<OnboardingStatus>("/api/v1/profile/onboarding-status");
    return data;
  },

  /** Self-serve re-prompt admins to verify this account. Rate-limited to
   * once per 24h server-side — a repeat call within the window returns
   * status "already_requested" instead of erroring.
   *
   * Sellers additionally need a minimum business profile (GST/company
   * details, office location, team size, years in business) filled in — a
   * plain call with an incomplete profile returns "incomplete_profile" with
   * the list of what's missing and sends nothing; `confirmIncomplete: true`
   * (seller said "yes, send automatically once I finish these") stashes that
   * intent server-side instead ("pending_profile_completion") — the backend
   * fires the real request itself the next time a profile save leaves the
   * business profile complete. */
  async requestVerification(confirmIncomplete = false): Promise<{
    status: "requested" | "already_requested" | "incomplete_profile" | "pending_profile_completion";
    message: string;
    requested_at?: string;
    missing_fields?: { key: string; label: string; step_key: string }[];
  }> {
    const { data } = await apiClient.post(
      "/api/v1/profile/request-verification",
      {},
      { params: confirmIncomplete ? { confirm_incomplete: true } : undefined },
    );
    return data;
  },

  validateImageFile(file: File): { valid: boolean; error?: string } {
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid image format. Allowed: ${VALID_IMAGE_EXTENSIONS.join(", ")}`,
      };
    }
    if (file.size > 5 * 1024 * 1024) {
      return { valid: false, error: "Image size must be less than 5MB" };
    }
    return { valid: true };
  },

  /**
   * Upload profile photo via POST /api/v1/profile/photo (multipart).
   * Call this only as part of the profile save flow — not immediately on file
   * selection — so a picked-but-uncommitted photo never hits the network.
   */
  async uploadProfileImage(file: File): Promise<AuthUser> {
    const validation = this.validateImageFile(file);
    if (!validation.valid) throw new Error(validation.error || "Invalid image file");
    const form = new FormData();
    form.append("file", file, file.name);
    const { data } = await apiClient.post<AuthUser>("/api/v1/profile/photo", form);
    return data;
  },

  async removeProfilePhoto(): Promise<AuthUser> {
    const { data } = await apiClient.delete<AuthUser>("/api/v1/profile/photo");
    return data;
  },

  async getProfileImageDisplayUrl(key: string): Promise<string> {
    try {
      const { data } = await apiClient.get<{ url: string; key: string; expires_in: number }>(
        `/api/public/bindings/presigned-url?key=${encodeURIComponent(key)}`,
      );
      return data.url;
    } catch {
      return "";
    }
  },
};
